import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Button,
  Chip,
  IconButton,
  Alert,
  CircularProgress,
  Avatar,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Collapse,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  InputAdornment,
  Tooltip,
  Badge,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Assignment as AssignmentIcon,
  Person as PersonIcon,
  BugReport as BugIcon,
  Assignment as TaskIcon,
  Star as StoryIcon,
  Speed as SprintIcon,
  FilterList as FilterIcon,
  CloudUpload as CloudUploadIcon,
  ExpandMore as ExpandMoreIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import Layout from '../components/Layout/Layout';
import apiService from '../services/api';
import { Issue, Project, User, IssueType, Priority, BoardColumn } from '../types';
import CreateIssueModal from '../components/Common/CreateIssueModal';
import IssueDetailsModal from '../components/Common/IssueDetailsModal';
import PlanningPokerButton from '../components/PlanningPoker/PlanningPokerButton';

interface IssueWithProject extends Issue {
  project?: Project;
  assignee?: User;
}

interface ProjectWithIssues extends Project {
  issues: IssueWithProject[];
  totalIssues: number;
}

const Issues: React.FC = () => {
  const [projectsWithIssues, setProjectsWithIssues] = useState<ProjectWithIssues[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedIssue, setSelectedIssue] = useState<IssueWithProject | null>(null);
  const [detailsIssue, setDetailsIssue] = useState<IssueWithProject | null>(null);
  
  // Create Issue Modal State
  const [openCreateIssueModal, setOpenCreateIssueModal] = useState(false);
  const [selectedProjectForCreation, setSelectedProjectForCreation] = useState<Project | null>(null);

  // Import Issues State
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importProvider, setImportProvider] = useState<string>('');
  const [importing, setImporting] = useState(false);

  // Filtering and Search State
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedProject, setSelectedProject] = useState<ProjectWithIssues | null>(null);
  const [filterType, setFilterType] = useState<string>('all');
  const [filterPriority, setFilterPriority] = useState<string>('all');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [expandedProjects, setExpandedProjects] = useState<Set<string>>(new Set());

  useEffect(() => {
    loadAllIssues();
  }, []);

  const loadAllIssues = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Load all projects
      const projects = await apiService.getProjects();
      
      // Load issues for each project
      const projectsWithIssuesData: ProjectWithIssues[] = [];
      
      for (const project of projects) {
        try {
          const response = await apiService.getIssues({ projectId: project.id });
          const issuesData = Array.isArray(response.data) ? response.data.flat() : [];
          const issuesWithProject = issuesData.map(issue => ({
            ...issue,
            project
          })) as IssueWithProject[];
          
          projectsWithIssuesData.push({
            ...project,
            issues: issuesWithProject,
            totalIssues: issuesWithProject.length
          });
        } catch (err) {
          console.error(`Failed to load issues for project ${project.name}:`, err);
          projectsWithIssuesData.push({
            ...project,
            issues: [],
            totalIssues: 0
          });
        }
      }
      
      setProjectsWithIssues(projectsWithIssuesData);
      
      // Expand first project by default if any projects have issues
      const firstProjectWithIssues = projectsWithIssuesData.find(p => p.totalIssues > 0);
      if (firstProjectWithIssues) {
        setExpandedProjects(new Set([firstProjectWithIssues.id]));
      }
      
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load issues');
    } finally {
      setLoading(false);
    }
  };

  const handleProjectToggle = (projectId: string) => {
    const newExpanded = new Set(expandedProjects);
    if (newExpanded.has(projectId)) {
      newExpanded.delete(projectId);
    } else {
      newExpanded.add(projectId);
    }
    setExpandedProjects(newExpanded);
  };

  const handleCreateIssue = (project: Project) => {
    setSelectedProjectForCreation(project);
    setOpenCreateIssueModal(true);
  };

  const handleCloseCreateIssueModal = () => {
    setOpenCreateIssueModal(false);
    setSelectedProjectForCreation(null);
  };

  const getIssueTypeIcon = (type: string) => {
    switch (type.toLowerCase()) {
      case 'bug': return <BugIcon />;
      case 'task': return <TaskIcon />;
      case 'story': return <StoryIcon />;
      default: return <TaskIcon />;
    }
  };

  const getIssueTypeColor = (type: string) => {
    switch (type.toLowerCase()) {
      case 'bug': return 'error';
      case 'task': return 'primary';
      case 'story': return 'success';
      default: return 'default';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority.toLowerCase()) {
      case 'critical': return 'error';
      case 'high': return 'warning';
      case 'medium': return 'primary';
      case 'low': return 'default';
      default: return 'default';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'todo': return 'default';
      case 'in progress': return 'warning';
      case 'review': return 'info';
      case 'done': return 'success';
      default: return 'default';
    }
  };

  // Filter projects based on selected project
  const getFilteredProjects = (projects: ProjectWithIssues[]) => {
    if (!selectedProject) {
      return projects;
    }
    return projects.filter(project => project.id === selectedProject.id);
  };

  // Filter issues based on search and filter criteria
  const getFilteredIssues = (issues: IssueWithProject[]) => {
    return issues.filter(issue => {
      // Search filter
      const matchesSearch = !searchTerm || 
        issue.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        issue.issueKey?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        issue.description?.toLowerCase().includes(searchTerm.toLowerCase());

      // Type filter
      const matchesType = filterType === 'all' || issue.type.toLowerCase() === filterType.toLowerCase();

      // Priority filter
      const matchesPriority = filterPriority === 'all' || issue.priority?.toLowerCase() === filterPriority.toLowerCase();

      // Status filter
      const matchesStatus = filterStatus === 'all' || issue.status?.toLowerCase() === filterStatus.toLowerCase();

      return matchesSearch && matchesType && matchesPriority && matchesStatus;
    });
  };

  // Calculate total filtered issues across filtered projects
  const getTotalFilteredIssues = () => {
    const filteredProjects = getFilteredProjects(projectsWithIssues);
    return filteredProjects.reduce((total, project) => {
      return total + getFilteredIssues(project.issues).length;
    }, 0);
  };

  if (loading) {
    return (
      <Layout>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      </Layout>
    );
  }

  return (
    <Layout>
      {/* Header */}
      <Box sx={{ 
        mb: 3,
        background: (theme) => theme.palette.mode === 'dark' 
          ? 'rgba(15, 23, 42, 0.95)' 
          : 'rgba(255, 255, 255, 0.95)',
        backdropFilter: 'blur(20px)',
        borderBottom: (theme) => `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
        boxShadow: (theme) => theme.palette.mode === 'dark' 
          ? '0 4px 20px rgba(0, 0, 0, 0.3)' 
          : '0 4px 20px rgba(0, 0, 0, 0.1)',
        p: 3,
        borderRadius: 2,
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center' 
      }}>
        <Box>
          <Typography variant="h4" component="h1" sx={{ mb: 1 }}>
            All Issues
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {getTotalFilteredIssues()} issues across {getFilteredProjects(projectsWithIssues).length} projects
          </Typography>
        </Box>
        <Box>
          <IconButton onClick={loadAllIssues} disabled={loading} title="Refresh all issues">
            <RefreshIcon />
          </IconButton>
        </Box>
      </Box>

        {/* Filters */}
        <Paper sx={{ p: 2, mb: 2 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                placeholder="Search issues..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <Autocomplete
                options={projectsWithIssues}
                getOptionLabel={(option) => `${option.name} (${option.key})`}
                value={selectedProject}
                onChange={(event, newValue) => {
                  setSelectedProject(newValue);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Select project..."
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                )}
                renderOption={(props, option) => (
                  <Box component="li" {...props}>
                    <Box>
                      <Typography variant="body2" fontWeight="medium">
                        {option.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {option.key} • {option.methodology}
                      </Typography>
                    </Box>
                  </Box>
                )}
                clearOnEscape
                noOptionsText="No projects found"
              />
            </Grid>
            <Grid item xs={12} sm={4} md={2}>
              <FormControl fullWidth>
                <InputLabel>Type</InputLabel>
                <Select value={filterType} onChange={(e) => setFilterType(e.target.value)} label="Type">
                  <MenuItem value="all">All Types</MenuItem>
                  <MenuItem value="story">Story</MenuItem>
                  <MenuItem value="task">Task</MenuItem>
                  <MenuItem value="bug">Bug</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4} md={2}>
              <FormControl fullWidth>
                <InputLabel>Priority</InputLabel>
                <Select value={filterPriority} onChange={(e) => setFilterPriority(e.target.value)} label="Priority">
                  <MenuItem value="all">All Priorities</MenuItem>
                  <MenuItem value="critical">Critical</MenuItem>
                  <MenuItem value="high">High</MenuItem>
                  <MenuItem value="medium">Medium</MenuItem>
                  <MenuItem value="low">Low</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4} md={2}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} label="Status">
                  <MenuItem value="all">All Statuses</MenuItem>
                  <MenuItem value="backlog">Backlog</MenuItem>
                  <MenuItem value="todo">To Do</MenuItem>
                  <MenuItem value="in progress">In Progress</MenuItem>
                  <MenuItem value="review">Review</MenuItem>
                  <MenuItem value="done">Done</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <Button
                variant="outlined"
                startIcon={<FilterIcon />}
                onClick={() => {
                  setSearchTerm('');
                  setSelectedProject(null);
                  setFilterType('all');
                  setFilterPriority('all');
                  setFilterStatus('all');
                }}
                fullWidth
              >
                Clear Filters
              </Button>
            </Grid>
          </Grid>
        </Paper>

        {/* Projects with Issues */}
        {projectsWithIssues.length === 0 ? (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="h6" color="text.secondary">
              No projects found
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Create a project first to view issues.
            </Typography>
          </Paper>
        ) : getFilteredProjects(projectsWithIssues).length === 0 ? (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="h6" color="text.secondary">
              No projects match the current filters
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Try selecting a different project or clearing the filters to see all projects.
            </Typography>
          </Paper>
        ) : (
          <Box>
            {getFilteredProjects(projectsWithIssues).map((project) => {
              const filteredIssues = getFilteredIssues(project.issues);
              const isExpanded = expandedProjects.has(project.id);
              
              return (
                <Accordion 
                  key={project.id} 
                  expanded={isExpanded}
                  onChange={() => handleProjectToggle(project.id)}
                  sx={{ mb: 2 }}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                      <Box sx={{ flexGrow: 1 }}>
                        <Typography variant="h6" component="h2">
                          {project.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {project.description || 'No description available'}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mr: 2 }}>
                        <Badge badgeContent={filteredIssues.length} color="primary">
                          <Chip 
                            label={project.methodology} 
                            color="primary"
                            size="small"
                          />
                        </Badge>
                        <Button
                          size="small"
                          startIcon={<CloudUploadIcon />}
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedProjectForCreation(project);
                            setImportDialogOpen(true);
                          }}
                        >
                          Import
                        </Button>
                        <Button
                          size="small"
                          startIcon={<AddIcon />}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleCreateIssue(project);
                          }}
                        >
                          Add Issue
                        </Button>
                      </Box>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    {filteredIssues.length === 0 ? (
                      <Box sx={{ textAlign: 'center', py: 4 }}>
                        <Typography variant="body2" color="text.secondary">
                          {project.issues.length === 0 
                            ? 'No issues in this project' 
                            : 'No issues match the current filters'
                          }
                        </Typography>
                      </Box>
                    ) : (
                      <Grid container spacing={2}>
                        {filteredIssues.map((issue) => (
                          <Grid item xs={12} sm={6} md={4} lg={3} key={issue.id}>
                            <Card 
                              sx={{ 
                                height: '100%',
                                cursor: 'pointer',
                                transition: 'all 0.2s ease-in-out',
                                '&:hover': {
                                  transform: 'translateY(-4px)',
                                  boxShadow: '0 8px 25px rgba(0,0,0,0.15)',
                                }
                              }}
                              onClick={() => setDetailsIssue(issue)}
                            >
                              <CardContent sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
                                {/* Header with Issue Key and Type */}
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                                    {issue.issueKey}
                                  </Typography>
                                  <Chip
                                    icon={getIssueTypeIcon(issue.type)}
                                    label={issue.type}
                                    color={getIssueTypeColor(issue.type) as any}
                                    size="small"
                                    sx={{ fontSize: '0.7rem' }}
                                  />
                                </Box>

                                {/* Issue Title */}
                                <Typography 
                                  variant="h6" 
                                  sx={{ 
                                    fontWeight: 600, 
                                    mb: 2, 
                                    lineHeight: 1.3,
                                    overflow: 'hidden',
                                    display: '-webkit-box',
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: 'vertical',
                                    flexGrow: 1
                                  }}
                                >
                                  {issue.title}
                                </Typography>

                                {/* Status and Priority */}
                                <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                                  <Chip
                                    label={issue.status}
                                    color={getStatusColor(issue.status) as any}
                                    size="small"
                                    sx={{ fontSize: '0.7rem' }}
                                  />
                                  {issue.priority && (
                                    <Chip
                                      label={issue.priority}
                                      color={getPriorityColor(issue.priority) as any}
                                      size="small"
                                      variant="outlined"
                                      sx={{ fontSize: '0.7rem' }}
                                    />
                                  )}
                                </Box>

                                {/* Assignee */}
                                <Box sx={{ mb: 2 }}>
                                  {issue.assignee ? (
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                      <Avatar sx={{ width: 28, height: 28, mr: 1, fontSize: '0.8rem' }}>
                                        {issue.assignee.firstName?.[0]}{issue.assignee.lastName?.[0]}
                                      </Avatar>
                                      <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                                        {issue.assignee.firstName} {issue.assignee.lastName}
                                      </Typography>
                                    </Box>
                                  ) : issue.assignments && issue.assignments.length > 0 ? (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                      {issue.assignments.slice(0, 2).map((assignment: any, index: number) => (
                                        <Box key={assignment.id} sx={{ display: 'flex', alignItems: 'center' }}>
                                          <Avatar sx={{ width: 28, height: 28, fontSize: '0.8rem' }}>
                                            {assignment.user?.firstName?.[0]}{assignment.user?.lastName?.[0]}
                                          </Avatar>
                                          {index === 0 && (
                                            <Typography variant="body2" sx={{ ml: 1, fontSize: '0.8rem' }}>
                                              {assignment.user?.firstName} {assignment.user?.lastName}
                                            </Typography>
                                          )}
                                        </Box>
                                      ))}
                                      {issue.assignments.length > 2 && (
                                        <Typography variant="caption" color="text.secondary">
                                          +{issue.assignments.length - 2} more
                                        </Typography>
                                      )}
                                    </Box>
                                  ) : (
                                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                                      Unassigned
                                    </Typography>
                                  )}
                                </Box>

                                {/* Footer with Story Points and Planning Poker */}
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 'auto' }}>
                                  <Typography variant="caption" color="text.secondary">
                                    {issue.project?.name}
                                  </Typography>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    {issue.storyPoints && (
                                      <Chip 
                                        label={`${issue.storyPoints} SP`} 
                                        size="small" 
                                        sx={{ 
                                          fontSize: '0.7rem',
                                          backgroundColor: 'primary.light',
                                          color: 'primary.contrastText'
                                        }} 
                                      />
                                    )}
                                    <PlanningPokerButton
                                      issueId={issue.id}
                                      issueTitle={issue.title}
                                      issueDescription={issue.description}
                                      onEstimationComplete={(estimate) => {
                                        // Update the issue with new estimate
                                        const updatedIssue = { ...issue, storyPoints: estimate };
                                        setDetailsIssue(updatedIssue);
                                        loadAllIssues(); // Refresh the list
                                      }}
                                      variant="icon"
                                      size="small"
                                    />
                                  </Box>
                                </Box>
                              </CardContent>
                            </Card>
                          </Grid>
                        ))}
                      </Grid>
                    )}
                  </AccordionDetails>
                </Accordion>
              );
            })}
          </Box>
        )}

      {/* Create Issue Modal */}
      {selectedProjectForCreation && (
        <CreateIssueModal
          open={openCreateIssueModal}
          onClose={handleCloseCreateIssueModal}
          projectId={selectedProjectForCreation.id}
          defaultValues={{ type: IssueType.STORY, priority: Priority.MEDIUM }}
          onCreated={async () => { 
            await loadAllIssues();
            handleCloseCreateIssueModal();
          }}
        />
      )}

      {/* Import Issues Dialog */}
      <Dialog open={importDialogOpen} onClose={() => {
        setImportDialogOpen(false);
        setSelectedProjectForCreation(null);
      }} maxWidth="sm" fullWidth>
        <DialogTitle>
          Import Issues
          {selectedProjectForCreation && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Importing into: <strong>{selectedProjectForCreation.name}</strong>
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <FormControl fullWidth>
              <InputLabel>Source</InputLabel>
              <Select
                value={importProvider}
                label="Source"
                onChange={(e) => setImportProvider(e.target.value as any)}
              >
                <MenuItem value="jira_excel">Jira — Excel/CSV</MenuItem>
                <MenuItem value="ms_planner_excel">Microsoft Planner — Excel</MenuItem>
                <MenuItem value="clickup_excel">ClickUp — Excel/CSV</MenuItem>
                <MenuItem value="trello_excel">Trello — Excel/CSV</MenuItem>
              </Select>
            </FormControl>
            <Typography variant="body2" color="text.secondary">
              Select the source platform and upload your Excel/CSV file to import issues into this project.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setImportDialogOpen(false);
            setSelectedProjectForCreation(null);
          }} disabled={importing}>Close</Button>
          <Button 
            variant="contained" 
            disabled={!importProvider || importing}
            onClick={() => {
              // TODO: Implement actual import functionality
              console.log('Import issues for project:', selectedProjectForCreation?.id, 'from:', importProvider);
            }}
          >
            {importing ? 'Importing...' : 'Import Issues'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Issue Details Modal */}
      <IssueDetailsModal
        open={!!detailsIssue}
        onClose={() => setDetailsIssue(null)}
        issue={detailsIssue as any}
        onUpdate={async () => { await loadAllIssues(); }}
        onOpenIssue={(issue) => setDetailsIssue(issue)}
        boardColumns={[]}
      />
    </Layout>
  );
};

export default Issues;